import type {
  ConditionEntry,
  ConditionLeaf,
} from "@/components/ConditionBuilder/types";
import { isConditionGroup } from "./conditionTree";
import { getOptionLabel } from "./optionLabel";

/**
 * Collects leaf conditions from a possibly nested ConditionBuilder tree (read-only display).
 */
export function flattenConditionLeaves(
  entries: ConditionEntry[] | undefined,
): ConditionLeaf[] {
  if (!entries?.length) return [];
  const out: ConditionLeaf[] = [];
  for (const entry of entries) {
    if (isConditionGroup(entry)) {
      out.push(...flattenConditionLeaves(entry.conditions));
    } else {
      out.push(entry as ConditionLeaf);
    }
  }
  return out;
}

function formatValueItem(item: unknown): string {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  if (typeof item === "object") {
    const obj = item as { displayName?: string; name?: string; value?: string };
    return obj.displayName || obj.name || obj.value || "";
  }
  return String(item);
}

export function formatConditionLeafValue(leaf: ConditionLeaf): string {
  const v = leaf.value;
  if (v === undefined || v === null) return "—";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) {
    return v.map(formatValueItem).filter(Boolean).join(", ");
  }
  return formatValueItem(v) || String(v);
}

export function formatConditionLeafAttribute(leaf: ConditionLeaf): string {
  const label = getOptionLabel(leaf.attribute);
  if (!label) return "—";
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatConditionLeafOperator(leaf: ConditionLeaf): string {
  return leaf.operator?.name ?? leaf.operator?.id ?? "—";
}
